import type { Clubhouse_RoundSetting } from "../../types/leagues";
import {
  AutoConcede,
  AutoContinue,
  AutoGimme,
  GimmeDistance,
  GroundConditions,
  HandicapMode,
  HandicapPercentage,
  PinType,
  ScoringType,
  SkyConditions,
  TeeType,
  TimeOfDay,
  WindDirection,
  WindSpeed,
} from "../../enums";

// A generated round always sends a complete settings object. Without a
// preset, the condition randomizer has no base object, and sending only the
// weather fields causes the API's non-nullable DTO properties to bind to CLR
// zero values (notably stimp=0 and auto-concede=OFF).
const DEFAULT_ROUND_SETTINGS: Clubhouse_RoundSetting = {
  autoConcede: AutoConcede.DOUBLE_PAR,
  autoContinue: AutoContinue.OFF,
  autoDrop: false,
  autoGimmes: AutoGimme.OFF,
  gimmeDistance: GimmeDistance.OFF,
  handicapMode: HandicapMode.OFF,
  handicapPercentage: HandicapPercentage.ONE_HUNDRED,
  courseRatingSlope: true,
  mulligans: false,
  pinType: PinType.INTERMEDIATE,
  scoringType: ScoringType.STROKE,
  windSpeed: WindSpeed.CALM,
  stimpSetting: 10,
  mensTee: TeeType.WHITE,
  womensTee: TeeType.RED,
  groundConditions: GroundConditions.NORMAL,
  windDirection: WindDirection.SOUTHEAST,
  skyConditions: SkyConditions.SUNNY,
  timeConditions: TimeOfDay.TIME_AFTERNOON,
};

/**
 * Per-round condition variation for the "Randomize settings for me" option.
 *
 * Varies only the *conditions* — pin, wind, sky, ground, time of day. Handicap
 * and automation stay on whatever the base settings say, because those govern
 * how a round is scored and played: a league whose gimme distance or handicap
 * mode moved week to week would be unfair, not interesting.
 *
 * **Deterministic by round number, deliberately.** `generateRoundPlan` runs
 * inside a `useMemo` and its output is diffed into `seasonRounds`; a
 * `Math.random()` here would hand back different conditions on every
 * regeneration, so any unrelated re-render would rewrite every round (and the
 * diff-then-setValue effect in Season Setup would thrash). Keying off the round
 * number means round 3 always gets round 3's weather, the plan is stable, and
 * the result is testable.
 *
 * Stimp is left alone: green speed reads as a course-maintenance setting rather
 * than weather, and quietly varying it would surprise an operator who set it.
 */

// The playable middle of each range. Extremes are left out on purpose —
// randomizing a league into fog and 35mph wind is a worse round, not a varied
// one, and an operator who actually wants those can set them per round.
const PINS = [PinType.NOVICE, PinType.INTERMEDIATE, PinType.ADVANCED];
const WIND_SPEEDS = [
  WindSpeed.CALM,
  WindSpeed.LIGHT,
  WindSpeed.MODERATE,
  WindSpeed.STRONG,
];
const WIND_DIRECTIONS = [
  WindDirection.NORTH,
  WindDirection.NORTHEAST,
  WindDirection.EAST,
  WindDirection.SOUTHEAST,
  WindDirection.SOUTH,
  WindDirection.SOUTHWEST,
  WindDirection.WEST,
  WindDirection.NORTHWEST,
];
const SKIES = [
  SkyConditions.SUNNY,
  SkyConditions.MOSTLY_SUNNY,
  SkyConditions.MOSTLY_CLOUDY,
  SkyConditions.CLOUDY,
];
const GROUNDS = [
  GroundConditions.DRY,
  GroundConditions.NORMAL,
  GroundConditions.WET,
];
const TIMES = [
  TimeOfDay.TIME_MORNING,
  TimeOfDay.TIME_AFTERNOON,
  TimeOfDay.TIME_DUSK,
];

/**
 * A stable scatter of a round number into a bucket index.
 *
 * Plain `roundNumber % length` would march every field in lockstep, so a season
 * would read as one repeating pattern (and two fields with the same option
 * count would move together forever). The salt decorrelates the fields; the
 * mixing keeps consecutive rounds from landing in adjacent buckets.
 */
function bucket(roundNumber: number, salt: number, length: number): number {
  let x = ((roundNumber + 1) * 2654435761 + salt * 40503) >>> 0;
  x = (x ^ (x >>> 15)) >>> 0;
  x = Math.imul(x, 2246822519) >>> 0;
  x = (x ^ (x >>> 13)) >>> 0;
  return x % length;
}

/** The varied conditions for one round. Same round number, same conditions. */
export function conditionsForRound(
  roundNumber: number,
): Pick<
  Clubhouse_RoundSetting,
  | "pinType"
  | "windSpeed"
  | "windDirection"
  | "skyConditions"
  | "groundConditions"
  | "timeConditions"
> {
  return {
    pinType: PINS[bucket(roundNumber, 1, PINS.length)]!,
    windSpeed: WIND_SPEEDS[bucket(roundNumber, 2, WIND_SPEEDS.length)]!,
    windDirection:
      WIND_DIRECTIONS[bucket(roundNumber, 3, WIND_DIRECTIONS.length)]!,
    skyConditions: SKIES[bucket(roundNumber, 4, SKIES.length)]!,
    groundConditions: GROUNDS[bucket(roundNumber, 5, GROUNDS.length)]!,
    timeConditions: TIMES[bucket(roundNumber, 6, TIMES.length)]!,
  };
}

/**
 * The settings one generated round should carry.
 *
 * `base` is the league-wide starting point (the facility defaults). With
 * `vary`, this round's conditions are layered over it — so handicap and
 * automation still come from the base even when the weather doesn't.
 */
export function settingsForRound(
  roundNumber: number,
  base: Clubhouse_RoundSetting | undefined,
  vary: boolean,
): Clubhouse_RoundSetting | undefined {
  if (!vary) return base;
  return {
    ...DEFAULT_ROUND_SETTINGS,
    ...base,
    ...conditionsForRound(roundNumber),
  };
}
